// src/prototype/components/sep/SEPSectionDependenciesRisk.tsx

import React from 'react';
import { AlertTriangle, GitMerge } from 'lucide-react';
import { effortTierForBadge } from '../../config/opportunityBadge';

interface Finalist {
  id: string;
  title: string;
  implementationEffort: 'Low' | 'Medium' | 'High';
  category: string;
}

export interface SEPSectionDependenciesRiskProps {
  finalists: Finalist[];
  clientName: string;
}

interface RiskRow { risk: string; likelihood: 'Low' | 'Medium' | 'High'; impact: 'Low' | 'Medium' | 'High'; mitigation: string }
interface DepRow { dependency: string; type: string; initiatives: string[] }

const likelihoodColor = (l: string) => ({ Low: 'text-emerald-700 bg-emerald-50 border-emerald-100', Medium: 'text-amber-700 bg-amber-50 border-amber-100', High: 'text-rose-600 bg-rose-50 border-rose-100' })[l] ?? 'text-gray-600 bg-gray-50 border-gray-200';

const getRisksByEffort = (effort: string): RiskRow[] => {
  const base: RiskRow[] = [
    { risk: 'Sponsor alignment delays', likelihood: 'Medium', impact: 'High', mitigation: 'Establish executive sponsor and bi-weekly steering cadence from Day 1.' },
    { risk: 'Data quality or access issues', likelihood: 'Medium', impact: 'High', mitigation: 'Conduct data readiness assessment in Discovery phase before full commitment.' },
  ];
  if (effort === 'High') base.push(
    { risk: 'Scope creep during build phase', likelihood: 'High', impact: 'Medium', mitigation: 'Lock scope at Design Gate with signed executive sign-off before build begins.' },
    { risk: 'Change management resistance', likelihood: 'Medium', impact: 'High', mitigation: 'Engage clinical champions early; build adoption metrics into success criteria.' },
  );
  if (effort === 'Low') base[0].likelihood = 'Low';
  return base;
};

export const SEPSectionDependenciesRisk: React.FC<SEPSectionDependenciesRiskProps> = ({ finalists, clientName }) => {
  const sharedDeps: DepRow[] = [
    { dependency: 'EHR Data Access & API Integration', type: 'Technical', initiatives: finalists.map(f => f.title.split(' ').slice(0, 3).join(' ')) },
    { dependency: 'Analytics Infrastructure (Cloud / BI Platform)', type: 'Platform', initiatives: finalists.filter(f => effortTierForBadge({ id: f.id, implementationEffort: f.implementationEffort }) !== 'Low').map(f => f.title.split(' ').slice(0, 3).join(' ')) },
    { dependency: 'Clinical Operations Workflow Review', type: 'Organizational', initiatives: finalists.filter(f => f.category?.toLowerCase().includes('patient') || f.category?.toLowerCase().includes('clinical') || true).slice(0, 2).map(f => f.title.split(' ').slice(0, 3).join(' ')) },
  ];

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl p-6 mb-6">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-[3px] self-stretch rounded-full bg-[#FF6E4C] shrink-0 min-h-[40px]" />
        <div>
          <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-widest mb-0.5">Section 7</p>
          <h2 className="text-lg font-bold text-[#18365E] leading-tight">Dependencies & Risk Summary</h2>
          <p className="text-[11px] text-[#717182] mt-0.5">Shared portfolio dependencies and initiative-level risk mitigation</p>
        </div>
      </div>

      <p className="text-sm text-[#18365E]/80 leading-relaxed mb-6">
        The risk register below captures key execution risks across <strong>{clientName}</strong>'s initiative portfolio,
        along with mitigation strategies. Shared dependencies are highlighted to support coordinated planning across initiatives.
      </p>

      {/* Shared dependencies */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <GitMerge className="w-3.5 h-3.5 text-[#18365E]/60" />
          <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">Shared Portfolio Dependencies</p>
        </div>
        <div className="space-y-2.5">
          {sharedDeps.map((d, i) => (
            <div key={i} className="border border-[#e5e7eb] rounded-xl p-3.5 bg-[#F5F7FA]">
              <div className="flex items-start gap-2 mb-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#18365E] mt-1.5 shrink-0" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-xs font-semibold text-[#18365E]">{d.dependency}</p>
                    <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full bg-[#18365E]/[0.08] text-[#18365E]/60 border border-[#18365E]/10">{d.type}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 pl-3.5">
                {d.initiatives.map((init, ii) => (
                  <span key={ii} className="text-[10px] px-2 py-0.5 bg-white border border-[#e5e7eb] rounded-full text-[#717182]">{init}…</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Risk register */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-3.5 h-3.5 text-[#717182]/60" />
          <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">Risk Register</p>
        </div>
        <div className="border border-[#e5e7eb] rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#F5F7FA] border-b border-[#e5e7eb]">
                <th className="text-left px-4 py-2.5 font-semibold text-[#717182]">Risk</th>
                <th className="text-center px-3 py-2.5 font-semibold text-[#717182] w-24">Likelihood</th>
                <th className="text-center px-3 py-2.5 font-semibold text-[#717182] w-20">Impact</th>
                <th className="text-left px-3 py-2.5 font-semibold text-[#717182]">Mitigation</th>
              </tr>
            </thead>
            <tbody>
              {finalists.flatMap((f, fi) =>
                getRisksByEffort(effortTierForBadge({ id: f.id, implementationEffort: f.implementationEffort })).map((r, ri) => (
                  <tr key={`${fi}-${ri}`} className="border-b border-[#e5e7eb]/60 last:border-0">
                    <td className="px-4 py-3">
                      <p className="font-medium text-[#18365E]">{r.risk}</p>
                      <p className="text-[10px] text-[#717182] mt-0.5">{f.title.split(' ').slice(0, 4).join(' ')}…</p>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${likelihoodColor(r.likelihood)}`}>{r.likelihood}</span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${likelihoodColor(r.impact)}`}>{r.impact}</span>
                    </td>
                    <td className="px-3 py-3 text-[#18365E]/75 leading-relaxed">{r.mitigation}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SEPSectionDependenciesRisk;